import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FeedbackActivityRecorder,
  getBuildVersion,
  toFeedbackEnvironment,
} from '@jfw/angular/shared/common-components/feedback';
import { FeedbackClientContext, FeedbackSourcePortalEnum } from '@jfw/types';
import { environment } from '../../../environments/environment';
import { DealerUserService } from '../dealer-user/dealer-user.service';

/**
 * EXT-6239: the context attached to a dealer's feedback. Navigation, failures
 * and client details come from the shared FeedbackActivityRecorder.
 *
 * The account is the dealer's default dealer, for the dialog to show. The API
 * ignores it and stamps the account from the verified dealer instead.
 */
@Injectable({ providedIn: 'root' })
export class DealerFeedbackContextService {
  private readonly recorder = inject(FeedbackActivityRecorder);
  private readonly currentDealer = toSignal(
    inject(DealerUserService).currentDealer$,
    { initialValue: null },
  );

  public snapshot(): FeedbackClientContext {
    const deepest = this.recorder.deepestRoute();
    const dealerId = this.currentDealer()?.dealerId?.trim();

    return {
      portal: FeedbackSourcePortalEnum.DealerPortal,
      environment: toFeedbackEnvironment(environment.envVersion),
      app: { version: getBuildVersion() },
      // Dealer Portal routes carry no module metadata.
      module: null,
      page: {
        name: deepest?.data?.['seoParams']?.metaTitle ?? document.title,
        url: window.location.href,
        routePattern: this.recorder.routePattern(),
      },
      previousRoutes: this.recorder.previousRoutes(),
      client: this.recorder.clientInfo(),
      recentErrors: this.recorder.recentErrors(),
      account: dealerId ? { number: dealerId, name: '' } : null,
    };
  }
}
